import React, { useState } from 'react';
import { FaceSmileIcon } from '@heroicons/react/24/outline';
import { ReactionSummary, EMOJIS, EmojiType } from './types';
import { ReactionModal } from './ReactionModal';

interface ReactionButtonProps {
  playId: string;
  reactionSummary?: ReactionSummary;
  userReaction?: EmojiType | null;
  onReactionChange?: () => void;
}

export const ReactionButton: React.FC<ReactionButtonProps> = ({
  playId,
  reactionSummary,
  userReaction,
  onReactionChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getTopReactions = () => {
    if (!reactionSummary || reactionSummary.total === 0) {
      return [];
    }

    const counts = Object.entries(reactionSummary)
      .filter(([key]) => key !== 'total')
      .map(([emoji, count]) => ({ emoji, count: count as number }))
      .filter(({ count }) => count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    return counts;
  };

  const topReactions = getTopReactions();
  const hasReactions = topReactions.length > 0;

  const getEmojiDisplay = (emojiCode: string): string => {
    const emoji = EMOJIS.find(e => e.code === emojiCode);
    return emoji?.emoji || '😀';
  };

  const handleReactionUpdate = () => {
    if (onReactionChange) {
      onReactionChange();
    }
  };

  return (
    <>
      <button
        data-track="reaction_open"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        className="
          inline-flex items-center gap-2
          text-sm font-medium
          min-w-[44px] min-h-[44px]
          transition-opacity
          hover:opacity-70
          text-body dark:text-disabled
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md
        "
        aria-label={hasReactions ? 'View reactions' : 'React to this play'}
      >
        {hasReactions ? (
          <>
            {topReactions.map(({ emoji, count }) => {
              const isUserReaction = userReaction === emoji;
              return (
                <span
                  key={emoji}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                    isUserReaction
                      ? 'bg-primary-100 dark:bg-primary-900 ring-2 ring-primary-500'
                      : 'bg-surface-subtle dark:bg-gray-700'
                  }`}
                >
                  <span className="text-lg">{getEmojiDisplay(emoji)}</span>
                  <span className={`text-xs font-semibold ${
                    isUserReaction
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-body dark:text-faint'
                  }`}>{count}</span>
                </span>
              );
            })}
            <span className="inline-flex items-center px-2 py-1 rounded-full border border-edge-input dark:border-gray-600 border-dashed">
              <FaceSmileIcon className="h-5 w-5 text-muted dark:text-disabled" />
            </span>
          </>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full border border-edge-input dark:border-gray-600 border-dashed">
            <FaceSmileIcon className="h-5 w-5 text-muted dark:text-disabled" />
          </span>
        )}
      </button>

      <ReactionModal
        playId={playId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onReactionUpdate={handleReactionUpdate}
      />
    </>
  );
};
